/**
 * This is base match details component where we share logic between web and agent
 */
import store from '../../../store';
// components
import MatchOddComponent from '../../common/match/match-odd-component';
import TabMarketComponent from '../../widgets/match-details/common/component-match-details-tab-market';
import BetBuilderTab from '../../widgets/match-details/common/betbuilder/betbuilder-tab';
import SwishBetBuilderTab from '../../widgets/match-details/common/betbuilder/swish-betbuilder-tab.vue';
import { EmptyState, ICON_CLASS } from '../empty-state';
// services
import { MatchDetailsPrematchMetadataRequestService } from '../../widgets/match-details/prematch/service-match-details-prematch-metadata-request';
import { MatchDetailsPrematchOddsRequestService } from '../../widgets/match-details/prematch/service-match-details-prematch-odds-request';
import { MatchDetailsPrematchPlayerOddsRequestService } from '../../widgets/match-details/prematch/service-match-details-prematch-player-odds-request';
import { PlayerMarketsRequestService } from '@bto-services/api/shared/player-markets-request-service';
import { MatchDetailsPrematchScorecastRequestService } from '../../widgets/match-details/prematch/service-match-details-prematch-scorecast-request';
import { EventBus } from '../../../services/event-bus-service';
import BetBuilderTabsRequestService from '../../../services/betbuilder/betbuilder-tabs-request-service';
import SwishBetBuilderTabsRequestService from '@bto-services/api/shared/swish-match-details-request-service';
import { getSettings } from '@bto-services/api/shared/sb-settings';
import { changeItemOdd } from '@bto-services/betslip-core';
// models
import MatchDetailsPrematchModel from '../../widgets/match-details/prematch/model-match-details-prematch';
import MatchDetailsLiveModel from '../../widgets/match-details/live/model-match-details-live';
import TabModel from '../../widgets/match-details/common/model-match-details-tab';
import PlayerOddsModel from '../../widgets/match-details/prematch/model-match-details-prematch-player-odds';
import BetBuilderTabsModel from '../../../models/betbuilder/betbuilder-tabs-model';
import SwishBetBuilderMarketsModel from 'bto-models/match/details/betbuilder/swish-betbuilder-markets-model';
import { hubs } from '../../../models/signalr-core';
import { BETSLIP_SETTINGS } from 'bto-models/betslip';
import PlayerTabModel from 'bto-models/match/details/player/player-tab.model';
import { mapLivePlayerMarkets } from '@bto-services/live-player-markets';

export default {
  name: 'BaseMatchDetails',
  components: {
    MatchOddComponent,
    TabMarketComponent,
    EmptyState,
    BetBuilderTab,
    SwishBetBuilderTab,
  },
  props: {
    matchIdProp: {
      type: [Number, String],
      required: false,
      default: null,
    },
    matchTypeProp: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      matchType: undefined,
      match: { tabs: [], },
      selectedMarket: undefined,
      playerOdds: [],
      playerTab: {},
      scorecast: { odds: [], },
      EMPTY_STATE_ICON: ICON_CLASS.MATCH_DETAILS,
      loading: false,
      loadingTimeOut: null,
      noLiveEventsTimeout: APP_SETTINGS.settings.noLiveEventsTimeout || 0,
      BB_TAB_ID: -11,
      betBuilderTabs: {},
      swishBetBuilderTabs: {},
      betBuilderMarketsFlag: false,
      liveMatchBrid: 0,
    };
  },
  computed: {
    /**
     * Get outrights tab or null if no outrights for match
     */
    outrightMarketTab() {
      if (!this.match && !this.match.tabs) {
        return null;
      }
      return this.match.tabs.find(t => t.directOutrights.length > 0 || t.multiOutrights.length > 0);
    },
    /**
     * Get outrights tab id or -10 if it doesn't exist
     */
    outrightsMarketId() {
      return this.outrightMarketTab ? this.outrightMarketTab.id : -10;
    },
    /**
     * Show outrights if selectedMarket is all and we have outrights or if outrights tab is selected
     */
    showOutrights() {
      return (this.selectedMarket === null && this.outrightsMarketId !== -10) || this.selectedMarket === this.outrightsMarketId;
    },
    betBuilderFixture() {
      const match = this.match || {};
      const homeTeam = match.homeTeam;
      const awayTeam = match.guestTeam;

      let matchName = '';
      if (homeTeam && awayTeam) {
        matchName = `${homeTeam} vs ${awayTeam}`;
      }

      if (matchName && match.awayAtHome) {
        matchName = `${awayTeam} @ ${homeTeam}`;
      }

      return {
        matchName,
        sportId: match.sportId || 0,
        categoryId: match.categoryId || 0,
        tournamentId: match.tournamentId || 0,
        sportCastSportId: match.sportCastSportId || 0,
        swishFixtureId: match.swishFixtureId,
        matchId: +this.getMatchId(),
        id: this.sportCastFixtureId,
        eventCode: match.eventCode,
        tournamentName: match.tournamentName,
        categoryName: match.categoryName,
      };
    },
    sportCastFixtureId() {
      return this.$route.query.scfi || this.match.sportCastFixtureId;
    },
    swishFixtureId() {
      return this.$route.query.sm || this.match.swishFixtureId;
    },
  },
  watch: {
    /**
     * Refresh view whenever match changes
     */
    matchIdProp() {
      this.reset();

    },
    match(val) {
      if (val.matchId !== undefined) {
        this.loading = false;
      }
    },
  },
  mounted() {
    // only hide widgets if match details is not embedded (inline)
    !this.matchIdProp && store.commit('hideAfterRouteWidgets');

    EventBus.$on('newLiveMatchDetails', this.handleNewLiveMatchDetails);
    EventBus.$on('livePlayerMarkets', this.handlePlayerMarkets);
    this.initMatchDetails();
    EventBus.$on('clearSelectedOutcome', this.deselectOdd);
    EventBus.$on('highlightBetItem', this.selectOdd);
    EventBus.$on('clearMessages', this.deselectAllOdds);
  },
  beforeDestroy() {
    this.disconnectLiveDetails();
    EventBus.$off('newLiveMatchDetails', this.handleNewLiveMatchDetails);
    EventBus.$off('livePlayerMarkets', this.handlePlayerMarkets);
    EventBus.$off('clearSelectedOutcome', this.deselectOdd);
    EventBus.$off('highlightBetItem', this.selectOdd);
    EventBus.$off('clearMessages', this.deselectAllOdds);
  },
  methods: {
    checkLiveArray() {
      const liveMatches = window.btoSignalRAvailibility || [];
      const liveMatch = liveMatches.find(match => (+match.mid) === (+this.$route.params.matchId));
      if (liveMatch) {
        this.liveMatchBrid = liveMatch.brid;
        return 'live';
      }
      return 'prematch';
    },
    /**
    * Determine if match is prematch or live  
    * return true if correct match type false if incorrect
    */
    checkMatchType() {
      if (this.matchTypeProp) {
        if (this.matchTypeProp !== 'prematch' && this.matchTypeProp !== 'live') {
          console.error('Invalid match type (in prop)'); // eslint-disable-line
          return false;
        }
        this.matchType = this.matchTypeProp;
      } else {
        switch (this.$route.name) {
          case 'matchDetailsPrematch':
          case 'seoGame':
            this.matchType = 'prematch';
            break;
          case 'matchDetailsLive':
          case 'seoGameLive':
            this.matchType = 'live';
            break;
          default:
            console.error('Invalid match type (in route)'); // eslint-disable-line
            return false;
        }
      }
      return true;
    },
    /**
     * Method that sets prematch data for match
     */
    getPrematchData() {
      this.loading = true;
      // initialize prematch
      this.match = new MatchDetailsPrematchModel({});
      this.playerTab = {};

      let prematchPartialTypes = [
        {
          name: 'metadata',
          requestService: MatchDetailsPrematchMetadataRequestService,
          action: 'addMetadata',
        },
        {
          name: 'odds',
          requestService: MatchDetailsPrematchOddsRequestService,
          action: 'addOdds',
        },
      ];

      // send requests and compose match model
      prematchPartialTypes.forEach((partialType) => {
        let requestService = new partialType.requestService(); // eslint-disable-line
        requestService.getParams.MatchID = this.getMatchId();

        requestService.execute((response) => {
          if (!response.data) return;
          this.match[partialType.action](response.data);
          this.selectFirstTab();
        }, (error) => {
          console.log(error); // eslint-disable-line
        });
      });

      // get player odds (goal scorers)
      let playerOddsRequestService = new MatchDetailsPrematchPlayerOddsRequestService();
      playerOddsRequestService.getParams.MatchID = this.getMatchId();
      playerOddsRequestService.execute((response) => {
        if (!response.data) {
          return;
        }
        this.playerOdds = response.data.map(singlePlayerOdds => new PlayerOddsModel(singlePlayerOdds));
      }, (error) => {
        console.log(error); // eslint-disable-line
      }).finally(() => {
        this.loading = false;
      });

      let newPlayerOddsRequestService = new PlayerMarketsRequestService();

      newPlayerOddsRequestService.getParams.matchID = this.getMatchId();
      newPlayerOddsRequestService.execute((response) => {
        if (response.data.t.length) {
          this.playerTab = new PlayerTabModel(response.data);
        }
      }, (error) => {
        console.log(error); // eslint-disable-line
      }).finally(() => {
        this.loading = false;
      });

      // //Get BoostedOdds
      if (process.env.VUE_APP_IS_AGENT) {
        let boostedMatchID = this.getMatchId();
        store.dispatch('getBoostedOdds', {
          matches: [boostedMatchID],
        });
      }

      // get scorecast
      getSettings().then(val => {
        if (val.Settings[BETSLIP_SETTINGS.SCORECAST_ENABLED]) {
          let scorecastRequestService = new MatchDetailsPrematchScorecastRequestService();
          scorecastRequestService.getParams.MatchID = this.getMatchId();
          scorecastRequestService.execute((response) => {
            this.scorecast = new TabModel(response.data);
          }, (error) => {
            console.log(error); // eslint-disable-line
          });
        }
      });
    },
    /**
     * Reset match model to empty live details model and connect to live details hub
     */
    getLiveData() {
      // initialize live match
      this.loading = true;
      this.match = new MatchDetailsLiveModel({});
      this.playerTab = {};
      if (this.$root.signalrRequest ) {
        const brid = this.liveMatchBrid || this.matchIdProp || this.$route.params.matchId;
        if (this.$root.signalrRequest.checkHubConnection(hubs.liveDetailsHub)){
          this.$root.signalrRequest.connectToLiveMatchDetailsHub(brid);
        } else {
          this.$root.signalrRequest.liveDetailsBetradarId = brid; 
        }
      }
      clearTimeout(this.loadingTimeOut);
      this.loadingTimeOut = setTimeout(() => {
        this.loading = false;
      }, parseInt(this.noLiveEventsTimeout) * 1000);
    },
    getLiveStatisticData() {
      const brid = this.liveMatchBrid || this.matchIdProp || this.$route.params.matchId;
      if (this.$root.signalrRequest) {
        if (this.$root.signalrRequest.checkHubConnection(hubs.liveStatisticsHub)){
          this.$root.signalrRequest.connectToLiveStatisticHub(brid);
        } else {
          this.$root.signalrRequest.liveStatisticsBetradarId = brid;
        }
      }
    },
    async openBetBuilderOffer() {
      this.selectMarket(this.BB_TAB_ID);
      if (this.betBuilderMarketsFlag) {
        return;
      }
      const sbSettings = await getSettings();
      const settings = sbSettings.Settings || {};
      if (this.swishFixtureId) {
        const swishBetBuilderTabRequestService = new SwishBetBuilderTabsRequestService();
        swishBetBuilderTabRequestService.getParams.matchId = this.$route.params.matchId;
        swishBetBuilderTabRequestService.execute(resp => {
          const swishMarkets = new SwishBetBuilderMarketsModel(resp.data, { homeTeam: this.match.homeTeam, guestTeam: this.match.guestTeam });
          this.swishBetBuilderTabs = swishMarkets;
        }, () => {
          this.selectMarket(null);
        }).finally(() => {
          this.loading = false;
          this.betBuilderMarketsFlag = true;
        });
        return;
      }
      const betBuilderTabRequestService = new BetBuilderTabsRequestService();
      betBuilderTabRequestService.getParams.fixtureId = this.sportCastFixtureId;
      betBuilderTabRequestService.getParams.key = settings[BETSLIP_SETTINGS.BET_BUILDER_API_KEY];
      betBuilderTabRequestService.baseUrl = settings[BETSLIP_SETTINGS.SPORTCAST_API_URL];
      this.loading = true;
      betBuilderTabRequestService.execute((resp) => {
        if (resp.data && resp.data.PayLoad) {
          const betBuilderTabs = new BetBuilderTabsModel(resp.data.PayLoad);
          this.betBuilderTabs = betBuilderTabs.tabs;
        }
      }, () => {
        this.selectMarket(null);
      }).finally(() => {
        this.loading = false;
        this.betBuilderMarketsFlag = true;
      });
    },
    /**
     * Inititalize match data, called on mounted and match change
     */
    initMatchDetails() {
      const validMatchType = this.checkMatchType();
      if (this.$route.query.scfi || this.$route.query.sm) {
        this.openBetBuilderOffer();
      }
      if (!validMatchType) {
        return;
      }
      /**
       * Get match data
       */
      switch (this.matchType) {
        case 'prematch': {
          this.getPrematchData();
          return;
        }
        case 'live': {
          this.getLiveData();
          this.getLiveStatisticData();
          return;
        }
      }
    },
    updateMatchDetails() {
      const validMatchType = this.checkMatchType();
      if (!validMatchType) {
        return;
      }
      if (this.matchType === 'live') {
        return;
      }
      const newMatchType = this.checkLiveArray();
      if (this.matchType === newMatchType) {
        return;
      }
      this.matchType = newMatchType;
      /**
       * Get match data
       */
      switch (this.matchType) {
        case 'prematch': {
          this.getPrematchData();
          return;
        }
        case 'live': {
          this.getLiveData();
          this.getLiveStatisticData();
          this.selectMarket(null)
          return;
        }
      }
    },
    /**
     * Set live match data from live details hub
     * @param data data from hub
     */
    handleNewLiveMatchDetails(rawData) {
      const { data, items } = rawData;
      let selectedBetItems = [];
      const liveGroupTabs = store.getters.getLiveMatchGroupTabs(data.SportID);
      this.match = new MatchDetailsLiveModel(data, items, selectedBetItems, liveGroupTabs);
      this.selectFirstTab();
      if (selectedBetItems.length > 0) {
        selectedBetItems.forEach(si => {
          changeItemOdd(si.item, si.newOdd, si.isBetStop);
        });
      }
    },
    handlePlayerMarkets(rawData) {
      this.playerTab = mapLivePlayerMarkets(rawData, this.match);
    },
    /**
     * Set selected market id
     * @param {null|number|string} marketId 
     */
    selectMarket(marketId) {
      if (!marketId) {
        // show all markets
        this.selectedMarket = marketId;
        return;
      }

      if (isNaN(marketId)) {
        // for special markets such as 'Player', 'Scorecast', 'Cashout'
        // (that don't have IDs)
        this.selectedMarket = marketId;
        return;
      }

      // parsing <option/> market values
      this.selectedMarket = Number.parseInt(marketId, 10);
    },
    /**
     * Return match ID from props or from route variable  
     * for live matches this is the betradarId
     */
    getMatchId() {
      return this.matchIdProp || this.$route.params.matchId || null;
    },
    /**
     * Select the first tab or all markets tab
     * @param {boolean} openAllMarkets 
     */
    selectFirstTab(openAllMarkets = false) {
      // only occurs once
      if (this.selectedMarket === undefined && this.match.tabs.length) {
        if (openAllMarkets) {
          this.selectedMarket = null;
          return;
        }
        this.selectedMarket = this.match.tabs[0].id;
      }
    },
    /**
       * Add active class to single outcome
       * @param {string} oClass - Unique id (mod-${matchId}-${outcomeId}) to find outcome
       */
    deselectOdd(oClass) {
      this.toggleOutcome(oClass, false);
    },
    /**
     * Remove active class from single outcome
     * @param {string} oClass - Unique id (mod-${matchId}-${outcomeId}) to find outcome
     */
    selectOdd(oClass) {
      this.toggleOutcome(oClass, true);
    },
    /**
     * Toggle active outcome class
     * @param {string} oClass - Unique id (mod-${matchId}-${outcomeId}) to find outcome
     * @param {boolean} [isSelected] - Flag weather to add or remove active class
     */
    toggleOutcome(oClass, isSelected = false) {
      if (!this.$refs.matchOddsContainer) {
        return;
      }
      const activeOutcome = this.$refs.matchOddsContainer.querySelector(`#${oClass}`);
      if (activeOutcome) {
        const activeClass = process.env.VUE_APP_IS_AGENT ? 'bto-sb-mormarket-odd-active' : 'bto-sb-event-mormarket-market-odd-active';
        if (isSelected) {
          activeOutcome.classList.add(activeClass);
        } else {
          activeOutcome.classList.remove(activeClass);
        }
      }
    },
    /**
     * Remove active class from all active outcomes
     */
    deselectAllOdds() {
      if (!this.$refs.matchOddsContainer) {
        return;
      }
      const activeClass = process.env.VUE_APP_IS_AGENT ? 'bto-sb-mormarket-odd-active' : 'bto-sb-event-mormarket-market-odd-active';
      const activeOutcomes = this.$refs.matchOddsContainer.querySelectorAll(`.${activeClass}`);
      if (activeOutcomes) {
        activeOutcomes.forEach(ao => {
          ao.classList.remove(activeClass);
        });
      }
    },
    /**
     * Disconnect from live details hub on match change or beforeDestory
     */
    disconnectLiveDetails() {
      if (this.$root.signalrRequest && this.$root.signalrRequest.checkHubConnection(hubs.liveDetailsHub)) {
        this.$root.signalrRequest.disconnectFromLiveMatchDetailsHub();
      }
      if (this.$root.signalrRequest && this.$root.signalrRequest.checkHubConnection(hubs.liveStatisticsHub)) {
        this.$root.signalrRequest.disconnectFromLiveStatisticHub();
      }
    },
    /**
     * Reset state on match change and get data for new match
     */
    reset() {
      this.selectedMarket = undefined;
      this.livePlayerFailed = false;
      this.betBuilderTabs = {};
      this.swishBetBuilderTabs = {};
      this.betBuilderMarketsFlag = false;
      this.playerOdds = [];
      this.playerTab = {};
      this.scorecast = { odds: [], };
      this.disconnectLiveDetails();
      this.initMatchDetails();
      this.updateMatchDetails();
    },
    checkIfBoostedHaveOdds() {
      return this.$store.getters.getBoostedOdds.length > 0;
    },
  },
};